import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import { COLOR } from '../../helpers/theme';

export type PaginationProps = {
  url: string;
  totalItem: number;
  isLoading?: boolean;
  listPerPage: number;
  currentIndex: number;
};

const Pagination = (props: PaginationProps) => {
  const { totalItem, listPerPage, currentIndex, isLoading } = props;
  const isPageLoading = typeof props.isLoading === 'undefined' ? false : props.isLoading;

  const calculateMaxIndex = useCallback(() => {
    return Math.ceil(totalItem / listPerPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItem, listPerPage, isLoading]);

  const renderPageItems = useCallback(() => {
    const maxIndex = calculateMaxIndex();
    let itemLimit = 9;
    let indexComponents = [];
    let lowerLimit = 0;
    let higherLimit = 5;

    if (maxIndex <= itemLimit) {
      lowerLimit = 0;
      higherLimit = maxIndex;

      for (let i = lowerLimit; i < higherLimit; i++) {
        indexComponents.push(
          <Link
            key={`page-${i + 1}`}
            to={`${props.url}${i + 1}`}
            className={isPageLoading ? 'disabled' : currentIndex === i + 1 ? 'active' : ''}
          >
            {i + 1}
          </Link>
        );
      }
    } else {
      if (currentIndex > 4 && currentIndex <= maxIndex - 4) {
        lowerLimit = currentIndex - 2;
        higherLimit = currentIndex + 2;

        indexComponents.push(
          <Link key={`page-1`} className={isPageLoading ? 'disabled' : currentIndex === 1 ? 'active' : ''} to={`${props.url}1`}>
            {1}
          </Link>,
          <button key="ellipsis-lower" disabled onClick={() => {}}>
            ...
          </button>
        );

        for (let i = lowerLimit - 1; i < higherLimit; i++) {
          indexComponents.push(
            <Link 
              key={`page-${i + 1}`}
              to={`${props.url}${i + 1}`}
              className={isPageLoading ? 'disabled' : currentIndex === i + 1 ? 'active' : ''}
            >
              {i + 1}
            </Link>
          );
        }

        indexComponents.push(
          <button key="ellipsis-higher" disabled={true} onClick={() => {}}>
            ...
          </button>,
          <Link
            key={`page-${maxIndex}`}
            className={isPageLoading ? 'disabled' : currentIndex === maxIndex ? 'active' : ''}
            to={`${props.url}${maxIndex}`}
          >
            {maxIndex}
          </Link>
        );
      } else if ((currentIndex > 0 && currentIndex < 5) || (currentIndex > maxIndex - 5 && currentIndex <= maxIndex)) {
        lowerLimit = 0;
        higherLimit = maxIndex;
        let ellipsis = 4;
        let adjustEllipsis = 0;
        if (currentIndex === ellipsis) {
          ellipsis++;
          adjustEllipsis += 2;
        } else if (currentIndex === higherLimit - ellipsis + 1) {
          ellipsis--;
          adjustEllipsis -= 2;
        }

        for (let i = lowerLimit; i < higherLimit; i++) {
          if (i === ellipsis) {
            indexComponents.push(
              <button key={`page-${i + 1}`} disabled onClick={() => {}}>
                ...
              </button>
            );
            i = higherLimit - ellipsis - 1 + adjustEllipsis;
          } else {
            indexComponents.push(
              <Link
                key={`page-${i + 1}`}
                className={isPageLoading ? 'disabled' : currentIndex === i + 1 ? 'active' : ''}
                to={`${props.url}${i + 1}`}
              >
                {i + 1}
              </Link>
            );
          }
        }
      }
    }

    return indexComponents;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateMaxIndex, currentIndex, isLoading]);

  const renderPrevButton = () => {
    if (currentIndex === 1) {
      return (
        <button disabled>
          {'«'}
        </button>  
      );
    }

    return (
      <Link
        className={isPageLoading ? 'disabled' : ''}
        to={`${props.url}${currentIndex + 1}`}
      >
        {'«'}
      </Link>
    );
  }

  const renderNextButton = () => {
    if (currentIndex === maxIndex) {
      return (
        <button disabled>
          {'»'}
        </button>  
      );
    }

    return (
      <Link
        className={isPageLoading ? 'disabled' : ''}
        to={`${props.url}${currentIndex + 1}`}
      >
        {'»'}
      </Link>
    );
  }

  const maxIndex = calculateMaxIndex();

  return (
    <PaginationWrapper>
      {renderPrevButton()}
      {renderPageItems()}
      {renderNextButton()}
    </PaginationWrapper>
  );
};

export default Pagination;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 5px;
  padding-bottom: 5px;

  & > * {
    min-width: 30px;
    padding: 5px;
    color: #fff;
    cursor: pointer;
    font-size: 14px;
    margin-right: 3px;
    text-align: center;
    border-radius: 5px;
    transition: 0.15s all;
    border: 1px solid transparent;
    background-color: ${COLOR.PRIMARY};

    &:last-child {
      margin-right: 0px;
    }

    &.active,
    &:hover {
      color: ${COLOR.PRIMARY};
      background-color: #fff;
      border-color: ${COLOR.PRIMARY};
    }

    &:disabled,
    &.disabled {
      color: #fff;
      background-color: #a5a5a5;
      border-color: #969696;
    }
  }
`;